import { useRef, useState } from 'react'
import { ExternalLink, FlaskConical, Loader2, Trash2, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Lead } from './types'
import { AddToCampaignDialog } from './add-to-campaign-dialog'
import { LeadProfile } from './detail/lead-profile'
import { EnrichmentPanel } from './detail/enrichment-panel'
import { ConversationHistory } from './detail/conversation-history'
import { SignalTimeline } from './detail/signal-timeline'
import {
  useRunEnrichment,
  useLeadMessages,
  useCachedEnrichment,
  useLeadById,
} from './detail/use-lead-detail'
import { useDeleteLead } from './use-leads'

type LeadDetailProps = {
  readonly lead: Lead | null
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export function LeadDetail({ lead: leadProp, open, onOpenChange }: LeadDetailProps) {
  // Subscribe to the freshest lead from the query cache (populated by enrichment
  // refetch). Falls back to the prop while the detail query loads.
  const { data: freshLead } = useLeadById(open ? leadProp?.id ?? null : null)
  const lead = freshLead ?? leadProp
  const [addCampaignOpen, setAddCampaignOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const deleteLead = useDeleteLead()
  const tabsScrollRef = useRef<HTMLDivElement | null>(null)
  const dragState = useRef<{ active: boolean; startX: number; startScroll: number; moved: boolean }>({
    active: false,
    startX: 0,
    startScroll: 0,
    moved: false,
  })

  function onTabsPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const el = tabsScrollRef.current
    if (!el) return
    // Record the start position but do NOT call setPointerCapture yet —
    // capturing eagerly redirects subsequent click events away from the
    // child TabsTrigger and breaks tab switching for normal clicks.
    // Capture is only acquired in onPointerMove once the user has actually
    // dragged past the 3px threshold.
    dragState.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false }
  }

  function onTabsPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const state = dragState.current
    const el = tabsScrollRef.current
    if (!state.active || !el) return
    const dx = e.clientX - state.startX
    if (Math.abs(dx) > 3) {
      // Promote to a real drag: take pointer capture so further movement
      // tracks even if the cursor leaves the wrapper.
      if (!state.moved) {
        state.moved = true
        try { el.setPointerCapture(e.pointerId) } catch { /* unsupported / already captured */ }
      }
      el.scrollLeft = state.startScroll - dx
    }
  }

  function endTabsDrag(e: React.PointerEvent<HTMLDivElement>) {
    const el = tabsScrollRef.current
    const state = dragState.current
    if (el && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
    state.active = false
  }

  function onTabsClickCapture(e: React.MouseEvent<HTMLDivElement>) {
    // Suppress the click that follows a drag; pure clicks (moved === false)
    // pass through to the TabsTrigger and switch tabs.
    if (dragState.current.moved) {
      e.preventDefault()
      e.stopPropagation()
      dragState.current.moved = false
    }
  }

  const enrichMutation = useRunEnrichment(lead?.id ?? null, lead ? {
    linkedin_url: lead.linkedin_url,
    full_name: lead.full_name,
    first_name: lead.first_name,
    last_name: lead.last_name,
    company: lead.company,
    job_title: lead.job_title,
  } : undefined)
  const { data: messages = [], isLoading: messagesLoading } = useLeadMessages(
    open ? (lead?.id ?? null) : null,
  )
  const cachedEnrichment = useCachedEnrichment(lead?.id ?? null)

  if (!lead) return null

  const isEnriched = Boolean(lead.enriched_at) || Boolean(cachedEnrichment)
  const enrichmentData = cachedEnrichment

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[480px] max-w-full p-0 flex flex-col">
        {/* Visually hidden title for accessibility */}
        <SheetHeader className="sr-only">
          <SheetTitle>
            Lead detail for {lead.full_name ?? `${lead.first_name} ${lead.last_name}`}
          </SheetTitle>
        </SheetHeader>

        {/* Profile header */}
        <div className="px-5 pt-5 pb-4 border-b">
          <LeadProfile lead={lead} />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
          <div
            ref={tabsScrollRef}
            onPointerDown={onTabsPointerDown}
            onPointerMove={onTabsPointerMove}
            onPointerUp={endTabsDrag}
            onPointerCancel={endTabsDrag}
            onClickCapture={onTabsClickCapture}
            className="shrink-0 overflow-x-auto overscroll-x-contain cursor-grab active:cursor-grabbing select-none [scrollbar-width:thin]"
          >
            <TabsList className="px-5 bg-background w-max min-w-full flex-nowrap">
              <TabsTrigger value="overview" className="shrink-0">Overview</TabsTrigger>
              <TabsTrigger value="research" className="shrink-0">Research</TabsTrigger>
              <TabsTrigger value="conversation" className="shrink-0">Conversation</TabsTrigger>
              <TabsTrigger value="timeline" className="shrink-0">Timeline</TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1">
            <div className="px-5 py-4">
              <TabsContent value="overview">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      About
                    </h3>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      {lead.company && (
                        <p>
                          <span className="font-medium text-foreground">Company: </span>
                          {lead.company}
                        </p>
                      )}
                      {lead.industry && (
                        <p>
                          <span className="font-medium text-foreground">Industry: </span>
                          {lead.industry}
                        </p>
                      )}
                      {lead.strategy && (
                        <p>
                          <span className="font-medium text-foreground">Strategy: </span>
                          {lead.strategy.replace(/_/g, ' ')}
                        </p>
                      )}
                      {lead.notes && (
                        <p>
                          <span className="font-medium text-foreground">Notes: </span>
                          {lead.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Quick enrichment prompt if not enriched */}
                  {!isEnriched && (
                    <div className="rounded-lg border border-dashed p-3 space-y-2">
                      <p className="text-xs text-muted-foreground">
                        Run AI enrichment to unlock company signals, pain points, and strategy recommendations.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs w-full"
                        onClick={() => enrichMutation.mutate()}
                        disabled={enrichMutation.isPending}
                      >
                        {enrichMutation.isPending ? 'Researching…' : 'Run Enrichment'}
                      </Button>
                    </div>
                  )}

                  {isEnriched && enrichmentData && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Top Signals
                      </h3>
                      <ul className="space-y-1.5">
                        {enrichmentData.icp_fit_signals.slice(0, 3).map((signal) => (
                          <li key={signal} className="flex items-start gap-2 text-xs">
                            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
                            {signal}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="research">
                <EnrichmentPanel
                  isEnriched={isEnriched}
                  isLoading={enrichMutation.isPending}
                  enrichmentData={enrichmentData}
                  onRunEnrichment={() => enrichMutation.mutate()}
                />
              </TabsContent>

              <TabsContent value="conversation">
                <ConversationHistory messages={messages} isLoading={messagesLoading} />
              </TabsContent>

              <TabsContent value="timeline">
                <SignalTimeline lead={lead} />
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>

        {/* Footer actions */}
        <div className="border-t px-5 py-3 flex flex-col gap-2 shrink-0">
          {!isEnriched && (
            <Button
              size="sm"
              variant="outline"
              className="w-full text-xs border-dashed"
              onClick={() => enrichMutation.mutate()}
              disabled={enrichMutation.isPending}
            >
              {enrichMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FlaskConical className="h-3.5 w-3.5" />
              )}
              {enrichMutation.isPending ? 'Researching...' : 'Enrich Lead (1 credit)'}
            </Button>
          )}
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 text-xs" onClick={() => setAddCampaignOpen(true)}>
              <UserPlus className="h-3.5 w-3.5" />
              Add to Campaign
            </Button>
            <Button size="sm" variant="outline" asChild className="text-xs">
              <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
                LinkedIn
              </a>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
              onClick={() => setDeleteConfirmOpen(true)}
              aria-label="Delete lead"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <AddToCampaignDialog
          leadIds={[lead.id]}
          leadName={lead.full_name ?? `${lead.first_name} ${lead.last_name}`}
          leads={[lead]}
          open={addCampaignOpen}
          onOpenChange={setAddCampaignOpen}
        />
      </SheetContent>

      <Dialog open={deleteConfirmOpen} onOpenChange={(o) => !deleteLead.isPending && setDeleteConfirmOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete lead?</DialogTitle>
            <DialogDescription className="text-foreground/90">
              This permanently removes{' '}
              <span className="font-semibold">{lead.full_name ?? `${lead.first_name} ${lead.last_name}`}</span>
              {' '}and their conversation history. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteLead.error && (
            <p className="text-xs text-destructive">
              {deleteLead.error instanceof Error ? deleteLead.error.message : 'Failed to delete lead'}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLead.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  await deleteLead.mutateAsync(lead.id)
                  setDeleteConfirmOpen(false)
                  onOpenChange(false)
                } catch {
                  /* error surfaced above */
                }
              }}
              disabled={deleteLead.isPending}
            >
              {deleteLead.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-1" /> Delete Lead</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  )
}
